import { useEffect, useState } from 'react';
import { Phone, Settings, FileText, Film, Download, Pin, BellOff } from 'lucide-react';
import Avatar from '../../lib/avatar';
import { SECTION_COLOR, casSmeny, formatCasListu, druhyUcastnik } from './komunikace';

/**
 * „O konverzaci" — třetí sloupec chatu (desktop) a obsah modalu ⓘ (mobil,
 * norma §1g: co je v panelu důležité, musí být na telefonu dosažitelné).
 *
 * Jen informuje — kdo v konverzaci je, kdo je dnes na směně, co se v ní
 * sdílelo. Editace (členové, název) je v Nastavení, tedy v modalu.
 */
export default function OKonverzaci({ konverzaceId, detail, user, lide, onlineIds, isDark, onKarta, onNastaveni, kompaktni = false }) {
    const [soubory, setSoubory] = useState(null);

    useEffect(() => {
        if (!konverzaceId) return;
        setSoubory(null);
        fetch(`/api/chat/konverzace/${konverzaceId}/soubory`)
            .then(r => (r.ok ? r.json() : { celkem: 0, soubory: [] }))
            .then(setSoubory).catch(() => setSoubory({ celkem: 0, soubory: [] }));
    }, [konverzaceId]);

    if (!detail) return null;

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const nadpis = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 8px' };
    const radek = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 32, borderBottom: `1px solid ${cara}`, fontSize: 13 };

    const smenaPodle = new Map((lide || []).filter(c => c.smena).map(c => [c.id, c.smena]));
    const ucastnici = [...(detail.ucastnici || [])].sort((a, b) => {
        const sa = smenaPodle.has(a.id) ? 0 : 1, sb = smenaPodle.has(b.id) ? 0 : 1;
        return sa - sb || (a.jmeno || '').localeCompare(b.jmeno || '', 'cs');
    });
    const naSmene = ucastnici.filter(u => smenaPodle.has(u.id)).length;
    const online = ucastnici.filter(u => u.id !== user?.id && onlineIds?.has(u.id)).length;
    const druhy = druhyUcastnik(detail, user?.id);

    const obrazky = (soubory?.soubory || []).filter(s => s.je_obrazek || s.je_video);
    const dokumenty = (soubory?.soubory || []).filter(s => !s.je_obrazek && !s.je_video);

    const blok = { padding: kompaktni ? '14px 16px' : 0, marginBottom: kompaktni ? 0 : 18 };

    return (
        <div>
            {detail.typ === 'soukromy' && druhy ? (
                <div style={blok}>
                    <p style={nadpis}>Kontakt</p>
                    <button type="button" onClick={() => onKarta?.(druhy)} className="flex items-center w-full text-left"
                        style={{ gap: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                        <Avatar url={druhy.avatar_url} jmeno={druhy.jmeno} size={44} online={onlineIds?.has(druhy.id)} isDark={isDark} />
                        <div className="min-w-0">
                            <p className="font-semibold truncate" style={{ color: 'var(--text-primary)', margin: 0 }}>{druhy.jmeno}</p>
                            <p className="text-xs truncate" style={{ color: muted, margin: 0 }}>{druhy.pozice || 'Kolega'}</p>
                        </div>
                    </button>
                    <div style={{ marginTop: 10 }}>
                        <div style={radek}><span style={{ color: muted }}>Dnes</span><b style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {smenaPodle.has(druhy.id) ? `na směně ${casSmeny(smenaPodle.get(druhy.id).cas_od)}–${casSmeny(smenaPodle.get(druhy.id).cas_do)}` : 'nemá směnu'}
                        </b></div>
                        {druhy.telefon && (
                            <div style={radek}><span style={{ color: muted }}>Telefon</span>
                                <a href={'tel:' + druhy.telefon.replace(/\s+/g, '')} className="flex items-center gap-1.5 font-semibold" style={{ color: SECTION_COLOR, textDecoration: 'none' }}>
                                    <Phone size={13} /> {druhy.telefon}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={blok}>
                    <p style={nadpis}>{detail.typ === 'sdeleni' ? 'Sdělení' : detail.typ === 'kanal' ? 'Kanál' : 'Skupina'}</p>
                    {detail.pravidlo && (
                        <p className="text-xs" style={{ color: muted, margin: '-2px 0 8px' }}>Členové automaticky: {detail.pravidlo}</p>
                    )}
                    <div style={radek}><span style={{ color: muted }}>Členů</span><b style={{ color: 'var(--text-primary)' }}>{ucastnici.length}</b></div>
                    <div style={radek}><span style={{ color: muted }}>Teď online</span><b style={{ color: 'var(--text-primary)' }}>{online}</b></div>
                    <div style={radek}><span style={{ color: muted }}>Dnes na směně</span><b style={{ color: 'var(--text-primary)' }}>{naSmene}</b></div>
                    <div style={{ marginTop: 8, maxHeight: kompaktni ? 'none' : 260, overflowY: 'auto' }}>
                        {ucastnici.map(u => {
                            const s = smenaPodle.get(u.id);
                            return (
                                <button key={u.id} type="button" onClick={() => u.id !== user?.id && onKarta?.(u)} className="flex items-center w-full text-left"
                                    style={{ gap: 8, minHeight: kompaktni ? 44 : 34, background: 'none', border: 'none', padding: 0, cursor: u.id !== user?.id ? 'pointer' : 'default' }}>
                                    <Avatar url={u.avatar_url} jmeno={u.jmeno} size={24} online={onlineIds?.has(u.id)} isDark={isDark} />
                                    <span className="flex-1 min-w-0 truncate text-[13px]" style={{ color: 'var(--text-primary)' }}>{u.jmeno}{u.id === user?.id ? ' (vy)' : ''}</span>
                                    {u.role === 'admin' && detail.typ !== 'soukromy' && <span className="text-[10.5px]" style={{ color: muted }}>správce</span>}
                                    {s && <span className="text-[11.5px] tabular-nums" style={{ color: muted }}>{casSmeny(s.cas_od)}–{casSmeny(s.cas_do)}</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {detail.pripnute?.length > 0 && (
                <div style={blok}>
                    <p style={nadpis}>Připnuté · {detail.pripnute.length}</p>
                    {detail.pripnute.map(p => (
                        <div key={p.id} className="flex" style={{ gap: 8, padding: '7px 0', borderBottom: `1px solid ${cara}` }}>
                            <Pin size={13} style={{ color: SECTION_COLOR, flexShrink: 0, marginTop: 2 }} />
                            <div className="min-w-0">
                                <p className="text-[12.5px]" style={{ color: 'var(--text-primary)', margin: 0 }}>{p.obsah}</p>
                                <p className="text-[11px]" style={{ color: muted, margin: 0 }}>{p.autor} · {formatCasListu(p.cas)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div style={blok}>
                <p style={nadpis}>Sdílené{soubory ? ` · ${soubory.celkem}` : ''}</p>
                {soubory === null && <p className="text-xs" style={{ color: muted }}>Načítám…</p>}
                {soubory && soubory.celkem === 0 && <p className="text-xs" style={{ color: muted }}>Zatím nic — fotky a soubory z konverzace se ukážou tady.</p>}
                {obrazky.length > 0 && (
                    <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                        {obrazky.slice(0, 8).map(s => (
                            <a key={s.id} href={s.url} target="_blank" rel="noopener" title={s.nazev_original}
                                style={{ aspectRatio: '1', borderRadius: 6, overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {s.je_obrazek
                                    ? <img src={s.nahled_url || s.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    : <Film size={18} style={{ color: muted }} />}
                            </a>
                        ))}
                    </div>
                )}
                {dokumenty.slice(0, 5).map(s => (
                    <a key={s.id} href={`/api/chat/soubory/${s.id}`} target="_blank" rel="noopener" className="flex items-center"
                        style={{ gap: 8, minHeight: 36, borderBottom: `1px solid ${cara}`, textDecoration: 'none', color: 'var(--text-primary)', fontSize: 12.5 }}>
                        <FileText size={14} style={{ color: muted, flexShrink: 0 }} />
                        <span className="flex-1 min-w-0 truncate">{s.nazev_original}</span>
                        <span style={{ color: muted, fontSize: 11 }}>{formatCasListu(s.poslano_at)}</span>
                        <Download size={13} style={{ color: muted }} />
                    </a>
                ))}
            </div>

            {detail.ztlumeno && (
                <p className="flex items-center gap-1.5 text-xs" style={{ ...blok, color: muted }}><BellOff size={13} /> Ztlumeno — upozornění chodí jen na zmínky.</p>
            )}
            {onNastaveni && (
                <div style={blok}>
                    <button type="button" onClick={onNastaveni} className="flex items-center justify-center gap-1.5 w-full rounded-lg font-semibold"
                        style={{ height: kompaktni ? 44 : 34, fontSize: 13, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                        <Settings size={14} /> {detail.typ === 'soukromy' ? 'Upozornění' : detail.ucastnici?.find(u => u.id === user?.id)?.role === 'admin' ? 'Nastavení a členové' : 'Nastavení'}
                    </button>
                </div>
            )}
        </div>
    );
}
